import type { TreatmentCourseTemplateDraft } from '@/types/treatmentCourses';

/**
 * Tilladte spørgeskemaer shown in Værktøjer → Spørgeskemaer.
 *
 * A normal note reads the one-step aftaletype
 * (`allowedQuestionnaireIdsForAppointmentType`). A forløb note (Trin X af Y)
 * reads the current step, then Generelt, via
 * `resolveJournalQuestionnaireAllowList`.
 *
 * The journal tools are a soft suggestion, same as a normal note: they open
 * on that list (grouped by tag when the questionnaires have tags), and search
 * plus «Vis alle skemaer» can still reach the rest of the catalogue. Sending
 * from the journal does not refuse a form outside the list.
 *
 * The patient forløb step itself stays a hard lock. Settings say a
 * practitioner editing the step can only pick from Tilladte, and
 * forms-send-assignment rejects a send tagged with
 * `treatment_course_step_id` when that column is non-empty. A journal send
 * includes the step id only when the booked column would accept the form.
 * A form reached via search is still sent, attached to the appointment, so
 * the lock does not turn the suggestion into a refusal.
 */

export function normalizeQuestionnaireIds(ids: readonly string[] | null | undefined): string[] {
  if (!ids) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (typeof id !== 'string') continue;
    const trimmed = id.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

export function allowedQuestionnaireIdsForAppointmentType(
  template: Pick<TreatmentCourseTemplateDraft, 'steps'> | null | undefined,
): string[] {
  if (!template || template.steps.length !== 1) return [];
  return normalizeQuestionnaireIds(template.steps[0]?.allowed_forms_template_ids);
}

export type JournalQuestionnaireAllowSource = 'step' | 'general' | 'free';

/**
 * Step Tilladte first. Generelt Tilladte only when that list actually exists.
 * Otherwise frit valg: an empty list, and the tools keep the empty state.
 */
export function resolveJournalQuestionnaireAllowList(input: {
  stepAllowedIds?: readonly string[] | null;
  /**
   * Generelt Tilladte. Null or undefined means the forløb does not store a
   * Generelt list yet — do not invent one. An empty array is an explicit
   * frit valg at Generelt and is not shown as a suggestion.
   */
  generalAllowedIds?: readonly string[] | null;
}): { source: JournalQuestionnaireAllowSource; ids: string[] } {
  const stepIds = normalizeQuestionnaireIds(input.stepAllowedIds);
  if (stepIds.length > 0) return { source: 'step', ids: stepIds };
  const generalIds = normalizeQuestionnaireIds(input.generalAllowedIds);
  if (input.generalAllowedIds != null && generalIds.length > 0) {
    return { source: 'general', ids: generalIds };
  }
  return { source: 'free', ids: [] };
}

export type SnapshotStepAllowList = {
  /**
   * True when the booking snapshot stored `allowed_forms_template_ids` for
   * this step, including an explicit empty frit valg.
   */
  recorded: boolean;
  ids: string[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function snapshotSteps(snapshot: unknown): Record<string, unknown>[] {
  const root = asRecord(snapshot);
  if (!root || !Array.isArray(root.steps)) return [];
  return root.steps.filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === 'object' && !Array.isArray(item),
  );
}

function readSnapshotAllow(match: Record<string, unknown> | undefined): SnapshotStepAllowList {
  if (!match || !Object.prototype.hasOwnProperty.call(match, 'allowed_forms_template_ids')) {
    return { recorded: false, ids: [] };
  }
  const raw = match.allowed_forms_template_ids;
  const ids = Array.isArray(raw)
    ? normalizeQuestionnaireIds(raw.filter((id): id is string => typeof id === 'string'))
    : [];
  return { recorded: true, ids };
}

export function snapshotStepAllowList(
  snapshot: unknown,
  step: { template_step_id?: string | null; sort_order?: number | null } | null | undefined,
): SnapshotStepAllowList {
  if (!step) return { recorded: false, ids: [] };
  const steps = snapshotSteps(snapshot);
  const byId = step.template_step_id
    ? steps.find((row) => row.id === step.template_step_id)
    : undefined;
  const byOrder =
    step.sort_order != null
      ? steps.find((row) => Number(row.sort_order) === step.sort_order)
      : undefined;
  const idList = readSnapshotAllow(byId);
  const orderList = readSnapshotAllow(byOrder);
  // Trin N is the snapshot step at that position. An older id that still
  // points at another step must not hide this position's Tilladte.
  if (orderList.ids.length > 0) return orderList;
  if (idList.ids.length > 0) return idList;
  if (orderList.recorded) return orderList;
  if (idList.recorded) return idList;
  return { recorded: false, ids: [] };
}

/**
 * The step's own Tilladte.
 *
 * A non-empty booked column wins, then a non-empty booking snapshot. Both are
 * the list captured for this course.
 *
 * An empty snapshot is not Frit valg. The template JSON always writes
 * `allowed_forms_template_ids`, including `[]` when the join table was empty
 * at booking. Tilladte added on the step afterwards, or a template save that
 * minted new step ids, leave that `[]` in place. The live template step is
 * the list the step card counts, so it fills in when the copy is empty.
 * If that step is empty too, the result is Frit valg.
 */
export function bookedStepQuestionnaireIds(args: {
  columnIds?: readonly string[] | null;
  snapshot: SnapshotStepAllowList;
  /** Current template step. Used only when the booked copy and snapshot are empty. */
  liveTemplateIds?: readonly string[] | null;
}): string[] {
  const column = normalizeQuestionnaireIds(args.columnIds);
  if (column.length > 0) return column;
  if (args.snapshot.ids.length > 0) return args.snapshot.ids;
  return normalizeQuestionnaireIds(args.liveTemplateIds);
}

export function templateStepAllowedIds(
  template:
    | {
        steps: readonly {
          clientId?: string;
          sort_order?: number | null;
          allowed_forms_template_ids?: readonly string[] | null;
        }[];
      }
    | null
    | undefined,
  step: { template_step_id?: string | null; sort_order?: number | null } | null | undefined,
): string[] {
  if (!template || !step) return [];
  const byId = step.template_step_id
    ? template.steps.find((row) => row.clientId === step.template_step_id)
    : undefined;
  const bySort =
    step.sort_order != null
      ? template.steps.find((row) => row.sort_order === step.sort_order)
      : undefined;
  const index = step.sort_order != null ? step.sort_order - 1 : -1;
  const byIndex = index >= 0 ? template.steps[index] : undefined;
  const positional = bySort ?? byIndex;
  const positionIds = normalizeQuestionnaireIds(positional?.allowed_forms_template_ids);
  const idIds = normalizeQuestionnaireIds(byId?.allowed_forms_template_ids);
  // Trin N reads the template step in that position, including after a save
  // mints new step ids. A stale id that still matches an empty step must not
  // hide the Tilladte now stored on this position.
  if (positionIds.length > 0) return positionIds;
  return idIds;
}

function readStoredIdList(value: object | null | undefined, key: string): string[] | null {
  if (!value || !Object.prototype.hasOwnProperty.call(value, key)) return null;
  const raw = (value as Record<string, unknown>)[key];
  if (!Array.isArray(raw)) return null;
  return normalizeQuestionnaireIds(raw.filter((id): id is string => typeof id === 'string'));
}

/**
 * Generelt Tilladte for a forløb.
 *
 * Indstillinger does not store a course-level list yet (Generelt has no
 * questionnaires). This returns null until `general_allowed_forms_template_ids`
 * is present on the booked course or its template. Do not substitute a list.
 *
 * An empty array means Generelt was saved as frit valg. Null means the field
 * is not stored. Step Tilladte still win; this list is only the fallback.
 */
export function generalAllowedQuestionnaireIds(
  source?: {
    course?: object | null;
    template?: object | null;
  } | null,
): string[] | null {
  const fromCourse = readStoredIdList(source?.course, 'general_allowed_forms_template_ids');
  if (fromCourse !== null) return fromCourse;
  return readStoredIdList(source?.template, 'general_allowed_forms_template_ids');
}

export function journalSendChecklist<T extends { id: string; name: string }>(args: {
  query: string;
  allowedIds: readonly string[];
  library: readonly T[];
}): T[] {
  const q = args.query.trim().toLowerCase();
  if (q) {
    return args.library.filter((row) => row.name.toLowerCase().includes(q));
  }
  const byId = new Map(args.library.map((row) => [row.id, row]));
  const rows: T[] = [];
  for (const id of args.allowedIds) {
    const row = byId.get(id);
    if (row) rows.push(row);
  }
  return rows;
}
